import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import { settingsService, auditService } from '../../../lib/services';

const SecuritySettings = () => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const [ipWhitelist, setIpWhitelist] = useState(false);
  const [ipWhitelistValue, setIpWhitelistValue] = useState('');
  const [auditLogging, setAuditLogging] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const sessionOptions = [
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '120', label: '2 hours' },
    { value: '240', label: '4 hours' }
  ];

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [settingsRes, logsRes] = await Promise.all([
          settingsService?.getByCategory('security'),
          auditService?.getAll({ limit: 50 })
        ]);

        if (!settingsRes?.error && settingsRes?.data) {
          settingsRes?.data?.forEach(s => {
            if (s?.settingKey === 'security_2fa_enabled') setTwoFactorEnabled(s?.settingValue === 'true');
            if (s?.settingKey === 'security_session_timeout') setSessionTimeout(s?.settingValue || '30');
            if (s?.settingKey === 'security_ip_whitelist_enabled') setIpWhitelist(s?.settingValue === 'true');
            if (s?.settingKey === 'security_ip_whitelist') setIpWhitelistValue(s?.settingValue || '');
            if (s?.settingKey === 'security_audit_logging') setAuditLogging(s?.settingValue !== 'false');
          });
        }

        if (!logsRes?.error && logsRes?.data) {
          setAuditLogs(logsRes?.data);
        }
      } catch (err) {
        console.error('Failed to load security settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const settings = [
        { settingKey: 'security_2fa_enabled', settingValue: String(twoFactorEnabled), settingType: 'boolean', category: 'security' },
        { settingKey: 'security_session_timeout', settingValue: sessionTimeout, settingType: 'number', category: 'security' },
        { settingKey: 'security_ip_whitelist_enabled', settingValue: String(ipWhitelist), settingType: 'boolean', category: 'security' },
        { settingKey: 'security_ip_whitelist', settingValue: ipWhitelistValue, settingType: 'string', category: 'security' },
        { settingKey: 'security_audit_logging', settingValue: String(auditLogging), settingType: 'boolean', category: 'security' },
      ];
      const { error } = await settingsService?.upsert(settings);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save security settings');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredLogs = auditLogs?.filter(log => {
    if (!searchQuery) return true;
    const q = searchQuery?.toLowerCase();
    return (
      log?.action?.toLowerCase()?.includes(q) ||
      log?.user?.fullName?.toLowerCase()?.includes(q) ||
      log?.user?.email?.toLowerCase()?.includes(q) ||
      log?.entityType?.toLowerCase()?.includes(q)
    );
  });

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      return new Date(ts)?.toLocaleString();
    } catch {
      return ts;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
        <span className="text-muted-foreground">Loading security settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <Icon name="CheckCircle" size={16} />
          Security settings saved successfully!
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          {saveError}
        </div>
      )}

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Lock" size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Authentication & Access</h3>
            <p className="text-sm text-muted-foreground">Configure security settings for user authentication</p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5">
          <Checkbox
            label="Enable Two-Factor Authentication (2FA)"
            description="Require users to verify their identity with a second factor"
            checked={twoFactorEnabled}
            onChange={(e) => setTwoFactorEnabled(e?.target?.checked)}
          />

          <Select
            label="Session Timeout"
            description="Automatically log out inactive users after this duration"
            options={sessionOptions}
            value={sessionTimeout}
            onChange={setSessionTimeout}
          />

          <Checkbox
            label="IP Whitelist"
            description="Restrict access to specific IP addresses only"
            checked={ipWhitelist}
            onChange={(e) => setIpWhitelist(e?.target?.checked)}
          />

          {ipWhitelist && (
            <Input
              label="Allowed IP Addresses"
              type="text"
              value={ipWhitelistValue}
              onChange={(e) => setIpWhitelistValue(e?.target?.value)}
              placeholder="192.168.1.0/24, 10.0.0.1"
              description="Comma-separated list of IP addresses or CIDR ranges"
            />
          )}
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Key" size={20} className="text-warning" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Password Policy</h3>
            <p className="text-sm text-muted-foreground">Define password requirements for all users</p>
          </div>
        </div>

        <div className="space-y-4">
          <Checkbox label="Require minimum 8 characters" checked readOnly />
          <Checkbox label="Require uppercase and lowercase letters" checked readOnly />
          <Checkbox label="Require at least one number" checked readOnly />
          <Checkbox label="Require at least one special character" readOnly />
          <Checkbox label="Prevent password reuse (last 5 passwords)" checked readOnly />
          <Input
            label="Password Expiration"
            type="number"
            placeholder="90"
            description="Force password change after this many days (0 = never)"
          />
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start justify-between mb-4 md:mb-6">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <Icon name="FileText" size={20} className="text-success" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Audit Logging</h3>
              <p className="text-sm text-muted-foreground">Track all user actions and system events</p>
            </div>
          </div>
          <Checkbox
            checked={auditLogging}
            onChange={(e) => setAuditLogging(e?.target?.checked)}
          />
        </div>

        {auditLogging && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="search"
                placeholder="Search audit logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="flex-1"
              />
              <Button variant="outline" iconName="Download" iconPosition="left" className="w-full sm:w-auto">
                Export
              </Button>
            </div>

            {filteredLogs?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {auditLogs?.length === 0 ? 'No audit logs found.' : 'No logs match your search.'}
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs md:text-sm font-semibold text-foreground">Action</th>
                        <th className="text-left px-4 py-3 text-xs md:text-sm font-semibold text-foreground">User</th>
                        <th className="text-left px-4 py-3 text-xs md:text-sm font-semibold text-foreground">Entity</th>
                        <th className="text-left px-4 py-3 text-xs md:text-sm font-semibold text-foreground">Timestamp</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredLogs?.slice(0, 20)?.map((log) => (
                        <tr key={log?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 text-sm text-foreground font-medium">{log?.action}</td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-foreground">{log?.user?.fullName || 'System'}</div>
                            <div className="text-xs text-muted-foreground">{log?.user?.email || '—'}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{log?.entityType}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{formatTimestamp(log?.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => window.location?.reload()}>
          Cancel
        </Button>
        <Button variant="default" iconName="Shield" iconPosition="left" loading={isSaving} onClick={handleSave}>
          Save Security Settings
        </Button>
      </div>
    </div>
  );
};

export default SecuritySettings;